import { appState, getCurrentCardState } from './state'
import { getCardPosition } from './cardPositions'
import { Card } from '../Models/Card'
import {
    undoButtonX,
    undoButtonY,
    newGameButtonX,
    newGameButtonY,
    recordsToggleButtonX,
    recordsToggleButtonY,
    stockCardX,
    stockCardY,
    menuButtonX,
    menuButtonY,
    autoCompleteButtonX,
    autoCompleteButtonY,
} from './allXYCoordinates'

/** Check whether point (x,y) falls inside a rectangle (strict inequality — edges excluded). */
export function isPointInRect(
    x: number,
    y: number,
    rx: number,
    ry: number,
    width: number,
    height: number
): boolean {
    return x > rx && x < rx + width && y > ry && y < ry + height
}

/** Check whether point (x,y) falls on a card, using its rendered position. */
function isPointOnCard(x: number, y: number, card: Card): boolean {
    const pos = getCardPosition(card)
    return isPointInRect(x, y, pos.x, pos.y, appState.cardWidth, appState.cardHeight)
}

export function getSelectedCard(x: number, y: number) {
    // console.log({ x, y })
    // 1. Loop through all cards in the tableau
    const tCard = findCardInTableau(x, y)
    if (tCard) {
        return tCard
    }
    // 2. Loop through all cards in the hand,
    const wCard = findCardInWaste(x, y)
    if (wCard) {
        return wCard
    }
    const sCard = findCardInStock(x, y)
    if (sCard) {
        return sCard
    }
    // 3. Loop through all the cards on foundations
    const fCard = findCardInFoundation(x, y)
    if (fCard) {
        return fCard
    }
}

function findCardInTableau(x: number, y: number) {
    const currentState = getCurrentCardState()
    // Iterate backward through cards because cards are drawn in array order
    // (index 0 first, then 1, etc.), so the highest index is visually on top.
    // For overlapping cards, we want to return the topmost one.
    for (let i = currentState.tableau.length - 1; i >= 0; i--) {
        for (let j = currentState.tableau[i].length - 1; j >= 0; j--) {
            const card = currentState.tableau[i][j]
            if (card && isPointOnCard(x, y, card)) {
                return currentState.tableau[i][j]
            }
        }
    }
}

function findCardInWaste(x: number, y: number) {
    const waste = getCurrentCardState().waste

    // Only allow interaction with the topmost card in waste
    if (waste.length === 0) return undefined

    const topCardIndex = waste.length - 1
    const card = waste[topCardIndex]

    if (card && isPointOnCard(x, y, card)) {
        return card
    }
}

function findCardInFoundation(x: number, y: number) {
    for (let i = getCurrentCardState().foundation.length - 1; i >= 0; i--) {
        for (let j = getCurrentCardState().foundation[i].length - 1; j >= 0; j--) {
            const card = getCurrentCardState().foundation[i][j]
            if (card && isPointOnCard(x, y, card)) {
                return getCurrentCardState().foundation[i][j]
            }
        }
    }
}

function findCardInStock(x: number, y: number) {
    for (let i = getCurrentCardState().stock.length - 1; i >= 0; i--) {
        const card = getCurrentCardState().stock[i]

        if (card && isPointOnCard(x, y, card)) {
            return getCurrentCardState().stock[i]
        }
    }
}

export function getStockSelected(x: number, y: number): boolean {
    return isPointInRect(x, y, stockCardX(), stockCardY(), appState.cardWidth, appState.cardHeight)
}

export function getUndoButtonSelected(x: number, y: number): boolean {
    return isPointInRect(x, y, undoButtonX(), undoButtonY(), appState.cardWidth, appState.cardWidth)
}

export function getSelectedNewGameButtonSelected(x: number, y: number): boolean {
    return isPointInRect(x, y, newGameButtonX(), newGameButtonY(), appState.cardWidth, appState.cardWidth)
}

export function getRecordsToggleButtonSelected(x: number, y: number): boolean {
    return isPointInRect(
        x,
        y,
        recordsToggleButtonX(),
        recordsToggleButtonY(),
        appState.cardWidth,
        appState.cardWidth
    )
}

export function getMenuButtonSelected(x: number, y: number) {
    return isPointInRect(x, y, menuButtonX(), menuButtonY(), appState.cardWidth, appState.cardWidth)
}

export function getAutoCompleteButtonSelected(x: number, y: number): boolean {
    return isPointInRect(
        x,
        y,
        autoCompleteButtonX(),
        autoCompleteButtonY(),
        appState.cardWidth,
        appState.cardWidth
    )
}
